import React, { useEffect, useState } from "react";
import dayjs from "dayjs";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import isoWeek from "dayjs/plugin/isoWeek";
import Button from "../Buttons/Button";
import CustomSelect from "../FormFields/Custom/CustomSelect";
import CustomSearch from "../FormFields/Custom/CustomSearch";
import DropdownMenu from "../Buttons/DropdownMenu";
import MonthPicker from "../FormFields/Custom/MonthPicker";
import DrawerAdv from "../Modal/DrawerAdv";
import TableDrawerData from "../Table/TableAdv/TableDrawerData";

import FormFieldSet from "../FormFields/FormFieldSet";
import { legends } from "./legends";
import TimeSheetFullData from "../../dummydata/TimeSheetFullData";
import { motion, AnimatePresence } from "framer-motion";
import TimeSheetTooltip from "./TimeSheetTooltip";
import Modal from "../Modal/Modal";
import ModalData from "./ModalData";
import { useDispatch, useSelector } from "react-redux";
import {
  getTimeSheetsDashboardStart,
  resetTimeSheetsDashboard,
  updateTimesheetStart,
} from "../../redux/slices/timesheets/timsheetSlice";
import { usePagination } from "../../hooks";
import { totalCountStart } from "../../redux/slices/totalCount/totalCountSlice";
import { useLoginUser } from "../../hooks/useLoginUser";
import { getEmployeeId } from "../../utils";
import Backdrop from "../Backgrounds/Backdrop";
import LogoLoader from "../Loading/LogoLoader";
import { showToast } from "../Toast/Toast";
import { formatCurrency } from "../../utils/stringUtils";
import axiosRequest from "../../redux/slices/universal/axiosService";
import { ENDPOINTS } from "../../redux/apiConfig";
import { debounce } from "lodash";
import { useCallback } from "react";
import { useMemo } from "react";
import CustomMonthPicker from "../FormFields/Custom/CustomMonthPicker";
import SmartTooltip from "../Info/SmartTooltip";

// Enable plugins
dayjs.extend(isSameOrBefore);
dayjs.extend(isoWeek);

const timeToDecimal = (timeStr) => {
  if (!timeStr) return 0;
  const [hours, minutes] = timeStr.split(":").map(Number);
  return Number((hours + minutes / 60).toFixed(2));
};

const TimesheetFull = () => {
  const [dashboardData, setDashboardData] = useState([]);
  const [dashboardCount, setDashboardCount] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const { userData: loginUserData } = useLoginUser();
  const loginUserUiid = loginUserData?.uiid;
  const { page, limit, setPage, handleLimitChange } = usePagination(
    15,
    (newPage, newLimit) => {
      // dispatch(
      //   getTimesheetsStart({
      //     page: newPage,
      //     limit: newLimit,
      //     filter: {},
      //     sort: { updatedAt: -1 },
      //     // sort: { uiid: 1 },
      //     // ret: "uiid businessName generalInfo email contact placements jobCode recordStatus",
      //     ref: "Employee Placement",
      //     // incl: "employeeId jobInfo.startDate jobInfo.endDate jobInfo.projectEndDate jobInfo.jobCode",
      //   })
      // );
    }
  );
  const { value } = useSelector((state) => state.totalCount);
  const { loading, timeSheetDashboard } = useSelector(
    (state) => state.timesheet
  );
  const totalCount = value?.timesheets?.data || 0; // Add null check with default empty array
  const dispatch = useDispatch();
  const options = [
    { label: "Showing 10 rows", value: 10 },
    { label: "Showing 25 rows", value: 25 },
    { label: "Showing 50 rows", value: 50 },
    { label: "Showing 100 rows", value: 100 },
  ];
  const [selectedDate, setSelectedDate] = useState(
    dayjs().subtract(1, "month")
  );
  const [selectTwoDates, setSelectTwoDates] = useState("");
  // console.log("selectTwoDates", selectTwoDates);
  const [expandedWeeks, setExpandedWeeks] = useState([]);
  const [activeCell, setActiveCell] = useState(null);
  // console.log("activeCell", activeCell);
  const [isDragging, setIsDragging] = useState(false);
  const [selectedCells, setSelectedCells] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [isLoading, setLoading] = useState(false);

  // Filter Drawer State
  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false);
  const [activeFilters, setActiveFilters] = useState(null);
  const filterColumns = useMemo(() => [
    { key: "employeeId.firstName", label: "Employee Name", dataType: "text" },
    { key: "employeeId.uiid", label: "Employee Code", dataType: "text" },
    { key: "clientId.businessName", label: "Client Name", dataType: "text" },
    { key: "placementType", label: "Placement Type", dataType: "text" },
    { key: "status", label: "Status", dataType: "text" },
    { key: "uiid", label: "Timesheet Code", dataType: "text" },
  ], []);

  const loadActiveFilters = useCallback(() => {
    try {
      if (loginUserUiid) {
        const storageKey = `tableConfig_timesheet-full-v1_${loginUserUiid}`;
        const rawData = localStorage.getItem(storageKey);
        if (rawData) {
          const tableConfig = JSON.parse(rawData);
          let active = null;
          
          if (Array.isArray(tableConfig)) {
            active = tableConfig.find(c => c && c.isActive === true);
          } else if (tableConfig && typeof tableConfig === "object") {
             for (const key in tableConfig) {
               if (tableConfig[key] && tableConfig[key].isActive === true) {
                 active = tableConfig[key];
                 break;
               }
             }
             if (!active && tableConfig.isActive) active = tableConfig;
          }

          if (active && active.isActive) {
            setActiveFilters({
              allFilterConditions: active.allFilterConditions || [],
              anyFilterConditions: active.anyFilterConditions || [],
              columns: active.columns || [],
              configName: active.configName,
              configId: active.id
            });
          } else {
            setActiveFilters(null);
          }
        } else {
          setActiveFilters(null);
        }
      }
    } catch (err) {
      console.error("Filter load error", err);
      setActiveFilters(null);
    }
  }, [loginUserUiid]);

  // Create/Sync default config
  useEffect(() => {
    if (loginUserUiid && filterColumns.length > 0) {
      const storageKey = `tableConfig_timesheet-full-v1_${loginUserUiid}`;
      const rawData = localStorage.getItem(storageKey);
      const currentCols = filterColumns.map(col => ({ key: col.key, label: col.label }));

      if (!rawData) {
        const initialConfig = [{
          id: Date.now(),
          configName: "Default Config",
          columns: currentCols,
          isActive: true,
          isDefault: true,
          allFilterConditions: [],
          anyFilterConditions: [],
          createdDate: new Date().toISOString()
        }];
        localStorage.setItem(storageKey, JSON.stringify(initialConfig));
        loadActiveFilters();
      } else {
        try {
          let configs = JSON.parse(rawData);
          if (!Array.isArray(configs)) configs = [configs];
          
          let defaultConfig = configs.find(c => c.configName === "Default Config" || c.isDefault);
          let systemConfig = configs.find(c => c.id === "system-generated-config" || c.isSystem);
          let changed = false;

          if (!defaultConfig) {
            defaultConfig = {
              id: Date.now(),
              configName: "Default Config",
              columns: currentCols,
              isActive: true,
              isDefault: true,
              allFilterConditions: [],
              anyFilterConditions: [],
              createdDate: new Date().toISOString()
            };
            configs.push(defaultConfig);
            changed = true;
          }

          // Ensure Default Config has correct columns and IS ACTIVE
          const areColsEqual = (a, b) => {
            if (!a || !b || a.length !== b.length) return false;
            return a.every(ac => b.some(bc => bc.key === ac.key));
          };

          if (!areColsEqual(defaultConfig.columns, currentCols)) {
            defaultConfig.columns = currentCols;
            changed = true;
          }

          if (!defaultConfig.isActive) {
            // Deactivate others
            configs.forEach(c => c.isActive = false);
            defaultConfig.isActive = true;
            changed = true;
          }

          if (changed) {
            localStorage.setItem(storageKey, JSON.stringify(configs));
            loadActiveFilters();
          }
        } catch (err) {
          console.error("Failed to sync default table config", err);
        }
      }
    }
  }, [loginUserUiid, filterColumns, loadActiveFilters]);

  useEffect(() => {
    loadActiveFilters();

    const handleStorageChange = (e) => {
      if (e.key === `tableConfig_timesheet-full-v1_${loginUserUiid}`) {
        loadActiveFilters();
      }
    };

    const handleCustomEvent = (e) => {
      if (e.detail?.tableId === "timesheet-full-v1" && e.detail?.userId === loginUserUiid) {
        loadActiveFilters();
      }
    };

    window.addEventListener("storage", handleStorageChange);
    window.addEventListener("tableConfigUpdated", handleCustomEvent);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener("tableConfigUpdated", handleCustomEvent);
    };
  }, [loadActiveFilters, loginUserUiid]);

  //
  // Get the first day of the month and find the previous Monday
  const firstDayOfMonth = selectedDate.startOf("month");
  const startOfFirstWeek = firstDayOfMonth.subtract(
    firstDayOfMonth.day() - 1,
    "day"
  );
  // Get the last day of the month and find the next Sunday
  const lastDayOfMonth = selectedDate.endOf("month");
  const endOfLastWeek = lastDayOfMonth.add(7 - lastDayOfMonth.day(), "day");
  // Filter data to only include work details within the selected month
  const filteredData = TimeSheetFullData.map((item) => ({
    ...item,
    workDetails: item.workDetails.filter((work) => {
      const workDate = dayjs(work.date);
      return workDate.isSame(selectedDate, "month");
    }),
  }));
  // Function to safely merge dailyEntries based on placementId.uiid
  function mergeEntries(data) {
    const mergedData = [];

    data.forEach((record) => {
      // Safely check if the required properties exist and are not undefined or null
      if (
        record &&
        record.placementId &&
        record.placementId.uiid &&
        Array.isArray(record.dailyEntries)
      ) {
        // Find existing record by placementId.uiid
        let existingRecord = mergedData.find(
          (d) => d.placementId?.uiid === record.placementId.uiid
        );

        // If no existing record is found, add the new record
        if (!existingRecord) {
          mergedData.push({ ...record }); // Create a shallow copy of the record
          existingRecord = mergedData[mergedData.length - 1]; // Now we have the reference to the newly added record
        }

        // Safely merge the dailyEntries, clone the array if needed to avoid mutation issues
        if (Array.isArray(record.dailyEntries)) {
          existingRecord.dailyEntries = existingRecord.dailyEntries.concat(
            record.dailyEntries
          );
        }
      }
    });

    return mergedData;
  }

  // Example input with optional chaining and default to empty array if undefined
  // const mergedData = mergeEntries(timeSheetDashboard?.data?.data || []);

  //working one
  // const mergedData = timeSheetDashboard?.data?.data || [];
  // for mapping
  // const mergedData =
  //   // timeSheetDashboard?.data?.data?.map((item) => ({
  //   dashboardData?.map((item) => ({
  //     dailyEntries: item?.dailyEntries || [],
  //     employeeId: {
  //       uiid: item?.employeeId?.uiid || "",
  //       employmentType: item?.employeeId?.employmentType || "",
  //       firstName: item?.employeeId?.firstName || "",
  //       lastName: item?.employeeId?.lastName || "",
  //       workAuthorization: item?.employeeId?.workAuthorization || "",
  //     },
  //     clientId: {
  //       businessName: item?.clientId?.businessName || "",
  //     },
  //     placementId: {
  //       clientPlacement: {
  //         placement: item?.placementId?.clientPlacement?.placement || "",
  //       },
  //     },
  //     invoiceId: item?.invoiceId || [],
  //     // timesheet status
  //     status: item?.status || "",
  //     // timesheet uiid
  //     uiid: item?.uiid || "",
  //     // timesheet status
  //   })) || [];
  const mergedData =
    // timeSheetDashboard?.data?.data?.map((item) => ({
    dashboardData?.map((item) => ({
      dailyEntries:
        item?.timesheets?.flatMap((timesheet) =>
          timesheet?.dailyEntries?.map((entry) => ({
            ...entry,
            timesheetId: timesheet.uiid,
            timesheetStatus: timesheet.status,
          }))
        ) || [],
      employeeId: {
        uiid: item?.employeeId?.uiid || item?.employeeId || "",
        employmentType: item?.employeeId?.employmentType || item?.employmentType || "",
        firstName: item?.employeeId?.firstName || item?.employeeName || "",
        lastName: item?.employeeId?.lastName || "",
        workAuthorization: item?.employeeId?.workAuthorization || item?.employeeWA || "",
      },
      clientId: {
        businessName: item?.clientId?.businessName || item?.clientName || "",
      },
      placementId: {
        clientPlacement: {
          placement: item?.placementId?.clientPlacement?.placement || item?.placement || item?.employeeWA || item?.placementType || "",
        },
      },
      placementType: item?.placementId?.clientPlacement?.placement || item?.placement || item?.employeeWA || item?.placementType || "",
      invoiceId:
        item?.timesheets?.flatMap((timesheet) => timesheet.invoiceId) || [],
      status: item?.timesheets?.flatMap((timesheet) => timesheet.status)?.[0] || "",
      // Capture ALL timesheet UIIDs for searching
      uiid: item?.timesheets?.map((timesheet) => timesheet.uiid).filter(Boolean).join(", ") || "",
    })) || [];

  const applyTimeSheetFilters = (data, filters) => {
    // If no filters or empty filters object, return all data immediately
    if (!filters || (!filters.allFilterConditions && !filters.filterConditions && !filters.anyFilterConditions && !filters.anyConditions)) {
      return data;
    }

    const allCondsRaw = filters.allFilterConditions || filters.filterConditions || [];
    const anyCondsRaw = filters.anyFilterConditions || filters.anyConditions || [];

    // Early exit if raw conditions are completely missing or empty arrays
    if (allCondsRaw.length === 0 && anyCondsRaw.length === 0) {
      return data;
    }

    // Helper to extract a searchable string from an observation
    const stringifyValue = (val) => {
      if (val === undefined || val === null) return "";
      // Handle array values (common in multi-selects or raw data)
      if (Array.isArray(val)) {
        return val.map(v => stringifyValue(v)).join(" ").trim();
      }
      if (typeof val === "object") {
        // Handle common object structures from selects or raw data
        return (val.label || val.value || val.firstName || val.businessName || JSON.stringify(val)).toString();
      }
      return val.toString();
    };

    const isValidCondition = (cond) => {
      // Must have both a column and an operator selected
      if (!cond?.filterColunm || !cond?.operator) return false;
      
      const noValueOps = ["isEmpty", "isNotEmpty", "isNull", "isNotNull"];
      if (noValueOps.includes(cond.operator)) return true;

      let raw = cond.filterValue;
      if (raw === undefined || raw === null || raw === "") {
        // Fallback to .value array if .filterValue is missing
        if (Array.isArray(cond.value)) {
          if (cond.value.length === 0) return false; // Empty array is invalid
          raw = cond.value[0];
        } else {
          raw = cond.value;
        }
      }
      
      const valStr = stringifyValue(raw).trim();
      // Ignore "ghost" conditions where no real searchable value was entered
      const invalidValues = ["", "[]", "{}", "[object Object]", "null", "undefined"];
      return !invalidValues.includes(valStr);
    };

    const allConditions = allCondsRaw.filter(isValidCondition);
    const anyConditions = anyCondsRaw.filter(isValidCondition);

    // If after cleaning up incomplete/invalid rows, we have nothing left, return all data
    if (allConditions.length === 0 && anyConditions.length === 0) {
      return data;
    }

    const getNestedValue = (obj, path) => {
      if (!obj) return "";
      
      if (path === "employeeId.firstName") {
        const first = obj?.employeeId?.firstName || "";
        const last = obj?.employeeId?.lastName || "";
        return `${last} ${first}`.trim();
      }
      
      return path.split('.').reduce((acc, part) => acc && acc[part], obj);
      
      if (path === "clientId.businessName") return obj?.clientId?.businessName || "";
      if (path === "placementId.clientPlacement.placement" || path === "placementType") {
        return obj?.placementType || obj?.placementId?.clientPlacement?.placement || "";
      }
      
      const parts = path.split('.');
      let current = obj;
      for (const part of parts) {
        if (current === null || current === undefined) return "";
        current = current[part];
      }
      
      return stringifyValue(current);
    };

    const checkCondition = (row, condition) => {
      const path = condition.filterColunm;
      const rawVal = getNestedValue(row, path);
      
      // Special handling for Employee Code to match both raw and formatted
      let valuesToMatch = [stringifyValue(rawVal)];
      if (path === "employeeId.uiid") {
          const rawId = row?.employeeId?.uiid || "";
          const formatted = getEmployeeId(rawId, row?.employeeId?.employmentType) || "";
          valuesToMatch = [stringifyValue(rawId), stringifyValue(formatted)];
      }

      // Special handling for Placement Type to look at all possible placement and work auth paths
      if (path === "placementType" || path === "placementId.clientPlacement.placement") {
          valuesToMatch = [
              stringifyValue(row?.placementType),
              stringifyValue(row?.placement),
              stringifyValue(row?.employeeWA),
              stringifyValue(row?.placementId?.clientPlacement?.placement)
          ].filter(v => v !== "");
      }

      let rawFilterVal = condition.filterValue;
      if (rawFilterVal === undefined || rawFilterVal === null || rawFilterVal === "") {
         rawFilterVal = Array.isArray(condition.value) && condition.value.length > 0 ? condition.value[0] : condition.value;
      }
      
      const filterValStr = stringifyValue(rawFilterVal).toLowerCase();
      const operator = String(condition.operator || "").toLowerCase();
      // Aggressive normalization: keep only alphanumeric for loose matching
      const norm = (s) => s.toLowerCase().replace(/[^a-z0-9]/g, "");

      const compare = (val) => {
          const strVal = String(val || "").toLowerCase();
          switch (operator) {
            case "contains": return strVal.includes(filterValStr) || norm(strVal).includes(norm(filterValStr));
            case "notcontains": return !strVal.includes(filterValStr) && !norm(strVal).includes(norm(filterValStr));
            case "equal": return strVal === filterValStr || norm(strVal) === norm(filterValStr);
            case "notequal": return strVal !== filterValStr && norm(strVal) !== norm(filterValStr);
            case "startswith": return strVal.startsWith(filterValStr) || norm(strVal).startsWith(norm(filterValStr));
            case "endswith": return strVal.endsWith(filterValStr) || norm(strVal).endsWith(norm(filterValStr));
            case "isempty": return !strVal || strVal.trim() === "";
            case "isnotempty": return !!strVal && strVal.trim() !== "";
            default: return true;
          }
      };

      return valuesToMatch.some(compare);
    };

    return data.filter(row => {
      // apply AND conditions (all must match)
      const passesAll = allConditions.length === 0 || allConditions.every(cond => checkCondition(row, cond));
      // apply OR conditions (any one must match)
      const passesAny = anyConditions.length === 0 || anyConditions.some(cond => checkCondition(row, cond));
      
      // Logic for combining AND and OR blocks:
      // If both sections are populated, we treat it as (AND results) AND (OR results)
      if (allConditions.length > 0 && anyConditions.length > 0) return passesAll && passesAny;
      if (allConditions.length > 0) return passesAll;
      return passesAny;
    });
  };

  const filteredMergedData = useMemo(() => applyTimeSheetFilters(mergedData, activeFilters), [mergedData, activeFilters]);

  // const filteredFullData = TimeSheetFullData.map((item) => ({
  //   ...item,
  //   workDetails: item.workDetails.filter((work) => {
  //     const workDate = dayjs(work.date);
  //     return workDate.isSame(selectedDate, "month");
  //   }),
  // }));
  // Output the merged data

  // Generate weeks and dates for table headers
  const weeks = [];
  let currentDate = startOfFirstWeek;
  let weekCounter = 1; // Add week counter
  while (currentDate.unix() <= endOfLastWeek.unix()) {
    const weekStart = currentDate.startOf("week");
    const weekDates = [];
    for (let i = 0; i < 7 && currentDate.unix() <= endOfLastWeek.unix(); i++) {
      weekDates.push(currentDate);
      currentDate = currentDate.add(1, "day");
    }
    weeks.push({
      weekNum: weekCounter, // Use weekCounter instead of isoWeek()
      start: weekStart,
      dates: weekDates,
    });
    weekCounter++; // Increment week counter
  }

  const toggleWeek = (weekNum) => {
    setExpandedWeeks((prev) =>
      prev.includes(weekNum)
        ? prev.filter((num) => num !== weekNum)
        : [...prev, weekNum]
    );
  };

  const handleDateChange = (date) => {
    if (!date) return;
    const newDate = dayjs(date);
    if (!newDate.isValid()) return;
    if (!newDate.isSame(selectedDate, "month")) {
      setSelectedDate(newDate);
      setSelectTwoDates(null);
    }
  };

  // Update the handleCellClick function
  const handleCellClick = (event, workDetail, date, itemName) => {
    let clickedDate = Number(date?.format("YYYYMMDD") || 0);
    // console.log("workDetail", workDetail);
    let timesheetId = workDetail?.dailyEntries?.find(
      (item) => item.date === clickedDate
    )?.timesheetId;
    // console.log("entryFormat", clickedDate,typeof clickedDate,timesheetId);
    event.stopPropagation();
    if (workDetail) {
      const rect = event.currentTarget.getBoundingClientRect();
      setActiveCell({
        workDetail,
        date,
        timesheetId,
        position: {
          x: rect.left,
          y: rect.top + window.scrollY,
          width: rect.width, // Add cell width
        },
        cellKey: `${date.format("YYYY-MM-DD")}-${itemName}`,
      });
    }
  };

  // Add new drag handlers
  const handleDragStart = (event, workDetail, date, itemName, userData) => {
    event.preventDefault();
    if (workDetail) {
      setIsDragging(true);
      setSelectedUser(userData);
      setSelectedCells([{ workDetail, date, itemName }]);
    }
  };

  const handleDragOver = (event, workDetail, date, itemName) => {
    event.preventDefault();
    if (isDragging && workDetail && selectedUser?.name === itemName) {
      if (
        !selectedCells.some(
          (cell) => cell.date.format("YYYY-MM-DD") === date.format("YYYY-MM-DD")
        )
      ) {
        setSelectedCells((prev) => [...prev, { workDetail, date, itemName }]);
      }
    }
  };

  const handleDragEnd = () => {
    setIsDragging(false);
    if (selectedCells.length >= 2) {
      setShowModal(true);
    } else {
      // Reset selection if less than 2 cells
      setSelectedCells([]);
      setSelectedUser(null);
    }
  };

  // Add click outside handler
  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (activeCell && !event.target.closest(".timesheet-tooltip")) {
        setActiveCell(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [activeCell]);

  const handleCloseTooltip = () => {
    setActiveCell(null);
  };

  // Close tooltip when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (activeCell && !event.target.closest(".timesheet-tooltip")) {
        setActiveCell(null);
      }
    };

    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, [activeCell]);

  const stylesList = {
    headPad: "px-4 py-1",
    cellPad: "px-4 py-4 border-[1px] border-gray-200 border-x-white",
    dateAccColWidth: "min-w-20 max-w-20",
    dateColWidth: "min-w-20 max-w-20",
  };

  useEffect(() => {
    if (selectTwoDates?.from) {
      const newSelected = dayjs(selectTwoDates.from);
      if (!newSelected.isSame(selectedDate, "month")) {
        setSelectedDate(newSelected);
      }
    }
  }, [selectTwoDates]);
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
  
      const [dashboardData, dashboardCount] = await Promise.all([
        axiosRequest({
          method: "GET",
          url: ENDPOINTS.PLACEMENTS.GET_ALL,
          params: {
            useAggregate: true,
            coll: "Placement",
            lim: limit,
            pg: page,
            fil: JSON.stringify({
              placementTS: {
                type: "AG_LOOKUP_DATA",
                value: [
                  selectedDate.startOf("month").format("YYYY-MM-DD"),
                  selectedDate.endOf("month").format("YYYY-MM-DD"),
                  searchTerm,
                ],
              },
            }),
          },
        }),
        axiosRequest({
          method: "GET",
          url: ENDPOINTS.PLACEMENTS.GET_ALL,
          params: {
            useAggregate: true,
            coll: "Placement",
            fil: JSON.stringify({
              placementTSCount: {
                type: "AG_LOOKUP_DATA",
                value: [
                  selectedDate.startOf("month").format("YYYY-MM-DD"),
                  selectedDate.endOf("month").format("YYYY-MM-DD"),
                  searchTerm,
                ],
              },
            }),
          },
        }),
      ]);
  
      setDashboardData(dashboardData?.data?.data || []);
      setDashboardCount(dashboardCount?.data?.data?.[0]?.TotalCount || 0);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  }, [limit, page, selectedDate, searchTerm]);
  
  const debouncedFetchData = useMemo(() => {
    return debounce(fetchData, 500); // 500ms delay
  }, [fetchData]);
  
  useEffect(() => {
    const startDate = selectedDate.startOf("month").local();
    const endDate = selectedDate.endOf("month").local();

    const fromDate = selectTwoDates?.from ?? startDate.toISOString();
    const toDate = selectTwoDates?.to ?? endDate.toISOString();

    const filters =
      selectTwoDates?.from && selectTwoDates?.to
        ? {
            period: {
              type: "RANGE",
              value: {
                from: selectTwoDates.from,
                to: selectTwoDates.to,
              },
            },
          }
        : {
            period: {
              type: "RANGE",
              value: {
                from: selectedDate.startOf("month").toISOString(),
                to: selectedDate.endOf("month").toISOString(),
              },
            },
          };

    debouncedFetchData();
    
    return () => {
      dispatch(resetTimeSheetsDashboard());
      debouncedFetchData.cancel();
    };
  }, [debouncedFetchData]);

  const handleActions = (action, timesheetId) => {
    const TimesheetStatus = {
      1: "Draft",
      2: "Submitted",
      3: "Approved",
      4: "Rejected",
      5: "Job Inactive",
      6: "Invoiced",
    };
    const convertedAction =
      action === "approve"
        ? 3
        : action === "reject"
        ? 4
        : action === "markAsInvoiced"
        ? 6
        : null;
    let payload;
    payload = [
      {
        type: "SET",
        value: {
          status: convertedAction,
        },
      },
      {
        type: "PUSH",
        value: {
          ...(convertedAction && {
            auditHistory: [
              {
                timestamp: new Date(),
                status: convertedAction,
                th: activeCell?.workDetail?.dailyEntries?.reduce(
                  (acc, item) => acc + item.th,
                  0
                ),
                st: activeCell?.workDetail?.dailyEntries?.reduce(
                  (acc, item) => acc + item.st,
                  0
                ),
                tbh: activeCell?.workDetail?.dailyEntries?.reduce(
                  (acc, item) => acc + item.tbh,
                  0
                ),
                updatedBy: loginUserUiid, // plug in your user identifier
              },
            ],
          }),
        },
      },
    ];
    // Conditionally add "period.from" and "period.to" filters only if selectTwoDates is provided
    const startDate = selectedDate.startOf("month").local();
    const endDate = selectedDate.endOf("month").local();

    const fromDate = selectTwoDates?.from ?? startDate.toISOString();
    const toDate = selectTwoDates?.to ?? endDate.toISOString();

    const filters = {
      period: {
        type: "RANGE",
        value: {
          from: fromDate,
          to: toDate,
        },
      },
    };
    switch (action) {
      case "approve":
      case "reject":
      case "markAsInvoiced": {
        // dispatch(
        //   updateTimesheetStatusStart({
        //     id: activeCell.workDetail?.uiid,
        //     status: 3,
        //   })
        // );
        dispatch(
          updateTimesheetStart({
            coll: "Timesheet",
            uiid: timesheetId,
            payload,
            onSuccess: async () => {
              // await Promise.all([
              //   dispatch(
              //     getTimeSheetsDashboardStart({
              //       page,
              //       limit,
              //       ret: "period dailyEntries uiid auditHistory status invoiceId",
              //       ref: "Employee Client Placement Invoice",
              //       // ref: "Employee Client Placement",
              //       incl: [
              //         "firstName lastName workAuth employmentType ",
              //         "businessName",
              //         "type clientPlacement.placement",
              //         "invoiceNum amounts",
              //       ],
              //       sort: {
              //         // uiid: -1,
              //         uiid: -1,
              //       },
              //       filters: filters,
              //     })
              //   ),
              //   dispatch(
              //     totalCountStart({
              //       collection: "timesheets",
              //       coll: "Timesheet",
              //       filters,
              //     })
              //   ),
              //   handleCloseTooltip(),
              // ]);
              fetchData();
            },
          })
        );
        break;
      }
      case "reset": {
        dispatch(
          updateTimesheetStart({
            coll: "Timesheet",
            uiid: timesheetId,
            payload: [
              {
                type: "SET",
                value: {
                  _skipFlatten: ["dailyEntries"],
                  dailyEntries: [],
                },
              },
            ],
            onSuccess: async () => {
              await Promise.all([
                // dispatch(
                //   getTimeSheetsDashboardStart({
                //     page,
                //     limit,
                //     ret: "period dailyEntries uiid auditHistory status invoiceId",
                //     ref: "Employee Client Placement Invoice",
                //     // ref: "Employee Client Placement",
                //     incl: [
                //       "firstName lastName workAuth employmentType ",
                //       "businessName",
                //       "type clientPlacement.placement",
                //       "invoiceNum amounts",
                //     ],
                //     sort: {
                //       // uiid: -1,
                //       uiid: -1,
                //     },
                //     filters: filters,
                //   })
                // ),
                // dispatch(
                //   totalCountStart({
                //     collection: "timesheets",
                //     coll: "Timesheet",
                //     filters,
                //   })
                // ),
                fetchData()
                // handleCloseTooltip(),
              ]);
            },
          })
        );
        break;
      }
      default:
        showToast("In Progress", "error");
        break;
    }
  };

  return (
    <div className="w-full h-full relative flex-1 flex flex-col">
      <AnimatePresence>
        {activeCell && (
          <TimeSheetTooltip
            handleActions={handleActions}
            position={activeCell?.position}
            // workDetail={activeCell.workDetail}
            workDetail={activeCell?.workDetail}
            timesheetId={activeCell?.timesheetId}
            selectedUser={selectedUser}
            selectedDate={selectedDate}
            onClose={handleCloseTooltip}
          />
        )}
      </AnimatePresence>
      <div className="timesheetToolbar flex justify-between items-center">
        <div className="flex items-center py-2 gap-2">
          <div className="text-sm">
            {dashboardCount > 0
              ? `${(page - 1) * limit + 1}-${Math.min(
                  page * limit,
                  dashboardCount
                )} of ${dashboardCount}`
              : "0-0 of 0"}
          </div>

          <div className="flex items-center gap-2">
            <Button
              type="icon"
              variant="text"
              color="black"
              icon="chevron_left"
              data-dropdown-persist="true"
              onClick={() => setPage(page - 1)}
              disabled={dashboardCount === 0 || page <= 1}
            />

            <Button
              type="icon"
              variant="text"
              color="black"
              icon="chevron_right"
              data-dropdown-persist="true"
              onClick={() => setPage(page + 1)}
              disabled={page * limit >= dashboardCount}
            />

            <div className="w-36">
              <CustomSelect
                size="sm"
                placeholder="Select rows"
                options={options}
                value={limit}
                data-dropdown-persist="true"
                onChange={(value) => handleLimitChange(value)}
              />
            </div>
          </div>
        </div>
        <div className="flex gap-2 items-center">
          <CustomMonthPicker
            selected={selectedDate}
            onChange={(date) => handleDateChange(date)}
            placeholder="Select month"
            size="sm"
            minDate={new Date("2022-05-01")}
            maxDate={new Date()}
          />
          <DropdownMenu
            menuAlign="right"
            minWidth="400px"
            MenuSection={
              <div
                className="flex flex-1 gap-2 px-2 py-1"
                data-dropdown-persist="true"
              >
                {/* <div className="flex-1">
                  <FormFieldSet
                    type="datepicker"
                    title=""
                    placeholder="Select Date"
                  />
                </div>
                <div className="flex-1">
                  <FormFieldSet
                    type="datepicker"
                    title=""
                    placeholder="Select Date"
                  />
                </div> */}
                <FormFieldSet
                  type="datepickerRange"
                  title="Choose Dates"
                  placeholder="Select From and To Date"
                  value={selectTwoDates}
                  onChange={(val) => {
                    setSelectTwoDates(val);
                  }}
                />
              </div>
            }
          >
            <div>
              {/* <Button variant="outlined" title={"Date Range"} size="sm" />*/}
            </div>
          </DropdownMenu>
        </div>
        <div className="flex items-center gap-0.5">
          <CustomSearch
            size="sm"
            value={searchTerm}
            placeholder="Search..."
            // rounded="rounded-full"
            onChange={(keyname,value) => setSearchTerm(value)}
            closeBtn
          />
          <Button type="icon" variant="text" color="black" icon="filter_alt" onClick={() => setIsFilterDrawerOpen(true)} />
          <DropdownMenu
            MenuSection={
              <div className="flex flex-col">
                <Button
                  title={"Option 1"}
                  variant="text"
                  color="black"
                  className="!justify-start py-1"
                />
                <Button
                  title={"Option 2"}
                  variant="text"
                  color="black"
                  className="!justify-start py-1"
                />
              </div>
            }
          >
            <Button icon="download" type="icon" variant="text" color="black" />
          </DropdownMenu>
        </div>
      </div>
      <div className="tableLegends my-3 flex flex-wrap gap-x-4 gap-y-2 items-center justify-end">
        {legends.map((item, index) => (
          <div key={index} className="flex items-center gap-1">
            <div className={`w-2.5 h-2.5 rounded-full ${item.color}`}></div>
            <span className="text-xs text-nowrap">{item.name}</span>
          </div>
        ))}
      </div>
      <div className="mainTable overflow-x-auto border-[1px] border-gray-200 overflow-auto flex-1">
        <table className="min-w-full border-collapse text-sm select-none ">
          <thead className="select-none  sticky top-0  z-[20]">
            <tr className="bg-primary-50">
              <th
                className={`font-medium text-nowrap min-w-30 max-w-80 text-left ${stylesList.headPad}`}
              >
                Sl No.
              </th>
              <th
                className={`font-medium text-nowrap min-w-30 max-w-80 text-left ${stylesList.headPad}`}
              >
                Code
              </th>
              <th
                className={`font-medium text-nowrap min-w-60 max-w-80 text-left ${stylesList.headPad}`}
              >
                Name
              </th>
              <th
                className={`font-medium text-nowrap min-w-60 max-w-80 text-left ${stylesList.headPad}`}
              >
                Client Name
              </th>
              <th
                className={`font-medium text-nowrap min-w-40 max-w-48 text-left ${stylesList.headPad}`}
              >
                Work Authorization
              </th>
              <th
                className={`font-medium text-nowrap min-w-30 max-w-60 text-left ${stylesList.headPad}`}
              >
                Placement Type
              </th>
              <th
                className={`font-medium text-nowrap min-w-40 max-w-48 text-left ${stylesList.headPad}`}
              >
                Invoice Number
              </th>
              <th
                className={`font-medium text-nowrap min-w-40 max-w-48 text-left ${stylesList.headPad}`}
              >
                Invoice Amount
              </th>
              <th
                className={`font-medium text-nowrap min-w-30 max-w-40 text-left ${stylesList.headPad}`}
              >
                Total Hours
              </th>
              {weeks.map((week, weekIndex) => {
                const isExpanded = expandedWeeks.includes(week.weekNum);
                return (
                  <React.Fragment key={weekIndex}>
                    <th
                      className={`font-normal cursor-pointer ${stylesList.dateAccColWidth} ${stylesList.headPad}`}
                      onClick={() => toggleWeek(week.weekNum)}
                    >
                      <div className="flex flex-col items-center justify-between">
                        <span>Week </span>
                        <span>{week.weekNum}</span>
                        <motion.span
                          className="material-icons !text-sm leading-3"
                          //animate={{ rotate: isExpanded ? 180 : 0 }}
                          transition={{ duration: 0.3 }}
                        >
                          {isExpanded ? "west" : "east"}
                        </motion.span>
                      </div>
                    </th>
                    <AnimatePresence initial={false}>
                      {isExpanded && (
                        <>
                          {week.dates.map((date, dateIndex) => (
                            <motion.th
                              key={`${weekIndex}-${dateIndex}`}
                              initial={{ width: 0, opacity: 0 }}
                              animate={{ width: "auto", opacity: 1 }}
                              exit={{ width: 0, opacity: 0 }}
                              transition={{ duration: 0.3, ease: "easeInOut" }}
                              className={`font-normal ${stylesList.headPad} ${
                                stylesList.dateColWidth
                              } ${
                                date.day() === 0 || date.day() === 6
                                  ? "!bg-neutrals-100 "
                                  : ""
                              }`}
                            >
                              <div className={``}>{date.format("MMM")}</div>
                              <div className={`text-primary`}>
                                {date.format("DD")}
                              </div>
                              <div className={``}>{date.format("ddd")}</div>
                              {/* <div
                                className={`text-base font-medium ${
                                  date.day() === 0 || date.day() === 6
                                    ? "text-blue-600"
                                    : ""
                                }`}
                              >
                                {date.format("DD")}
                              </div> */}
                            </motion.th>
                          ))}
                        </>
                      )}
                    </AnimatePresence>
                  </React.Fragment>
                );
              })}
            </tr>
          </thead>
          <tbody className="">
            {filteredMergedData.map((item, index) => {
              // Calculate total hours only for the selected month
              // Calculate total hours only for the selected month
              // const totalHours = item?.dailyEntries?.reduce((sum, work) => {
              //   // Ensure that work?.st is a valid number before splitting
              //   // const [hours, minutes] =
              //   //   //for st
              //   //   // typeof work?.st === "number" && work?.st > 0
              //   //   //for sth
              //   //   typeof work?.st === "number" && work?.th > 0
              //   //     ? work?.th?.toString().split(".").map(Number) || [0, 0]
              //   //     : [0, 0];

              //   // // Calculate total minutes from hours and minutes
              //   // const totalMinutes = hours * 60 + minutes;
              //   // return sum + totalMinutes;
              //   sum += work?.st;
              //   return sum;
              // }, 0);
              const totalHours = item?.dailyEntries?.reduce((sum, work) => {
                return sum + work?.st;
              }, 0);

              //mahendar
              // Format total hours as HH:mm
              // const formattedTotalHours = `${Math.floor(
              //   totalHours / 60
              // )}:${String(totalHours % 60).padStart(2, "0")}`;
              // const formattedTotalHours = dayjs
              //   .duration(safeTotal, "hours")
              //   .format("HH:mm");

              //working
              // const safeTotal = totalHours ? totalHours : 0;
              // const total = Number(safeTotal);
              // const hours = Math.floor(total);
              // const minutes = Math.round((total - hours) * 60);
              // const formattedTotalHours = `${hours}:${minutes
              //   .toString()
              //   .padStart(2, "0")}`;

              const formattedTotalHours = totalHours
                ? totalHours.toFixed(2)
                : 0;

              return (
                <tr key={index} className="hover:bg-gray-50">
                  <td className={`${stylesList.cellPad}`}>{index + 1}</td>

                  <td className={`${stylesList.cellPad}`}>
                    {`${getEmployeeId(item?.employeeId?.uiid, item?.employeeId?.employmentType)}`}
                  </td>

                  <td className={`${stylesList.cellPad}`}>
                    <SmartTooltip
                      description={`${[item?.employeeId?.lastName, item?.employeeId?.firstName].filter(Boolean).join(" ")}`}
                      size="xs"
                      color="black"
                      noicon
                      tooltipPosition="bottom"
                      portal
                    >
                      <span className="line-clamp-1">
                        {`${[item?.employeeId?.lastName, item?.employeeId?.firstName].filter(Boolean).join(" ")}`}
                      </span>
                    </SmartTooltip>
                  </td>

                  <td className={`${stylesList.cellPad}`}>
                    <SmartTooltip
                      description={item?.clientId?.businessName}
                      size="xs"
                      color="black"
                      noicon
                      tooltipPosition="bottom"
                      portal
                    >
                      <span className="line-clamp-1">
                        {item?.clientId?.businessName}
                      </span>
                    </SmartTooltip>
                  </td>

                  <td className={`${stylesList.cellPad}`}>
                    <SmartTooltip
                      description={item?.employeeId?.workAuthorization}
                      size="xs"
                      color="black"
                      noicon
                      tooltipPosition="bottom"
                      portal
                    >
                      <span className="line-clamp-1">
                        {item?.employeeId?.workAuthorization}
                      </span>
                    </SmartTooltip>
                  </td>

                  <td className={`${stylesList.cellPad}`}>
                    <SmartTooltip
                      description={
                        item?.placementId?.clientPlacement?.placement
                      }
                      size="xs"
                      color="black"
                      noicon
                      tooltipPosition="bottom"
                      portal
                    >
                      <span className="line-clamp-1">
                        {item?.placementId?.clientPlacement?.placement}
                      </span>
                    </SmartTooltip>
                  </td>

                  <td className={`${stylesList.cellPad}`}>
                    <SmartTooltip
                      description={item?.invoiceId
                        ?.flatMap((id) => id.invoiceNum)
                        .join(", ")}
                      size="xs"
                      color="black"
                      noicon
                      tooltipPosition="bottom"
                      portal
                    >
                      <span className="line-clamp-1">
                        {item?.invoiceId
                          ?.flatMap((id) => id.invoiceNum)
                          .join(", ")}
                      </span>
                    </SmartTooltip>
                  </td>
                  <td className={`${stylesList.cellPad}`}>
                    {/* {item.invoiceAmount} */}
                    {item?.invoiceId?.reduce(
                      (sum, work) => sum + Number(work?.amounts?.invoiced) || 0,
                      0
                    )
                      ? formatCurrency(
                          item?.invoiceId?.reduce(
                            (sum, work) =>
                              sum + Number(work?.amounts?.invoiced) || 0,
                            0
                          )
                        )
                      : "-"}
                  </td>
                  <td className={`${stylesList.cellPad}`}>
                    {formattedTotalHours ? formattedTotalHours : "-"}
                  </td>
                  {weeks.map((week, weekIndex) => {
                    const isExpanded = expandedWeeks.includes(week.weekNum);
                    // Calculate week total only for days within the selected month
                    const weekTotalMinutes = item.dailyEntries

                      .filter((work) => {
                        const workDateStr = String(work?.date); // e.g., "20250530"

                        const workYear = workDateStr.substring(0, 4);

                        const workMonth = workDateStr.substring(4, 6);

                        const workDay = workDateStr.substring(6, 8);

                        const formattedWorkDate = `${workDay}-${workMonth}-${workYear}`;

                        return week.dates.some((date) => {
                          const inputDate = new Date(date); // assuming `date` is a JS Date object

                          const day = String(inputDate.getDate()).padStart(
                            2,
                            "0"
                          );

                          const month = String(
                            inputDate.getMonth() + 1
                          ).padStart(2, "0");

                          const year = inputDate.getFullYear();

                          const formattedComponentDate = `${day}-${month}-${year}`;

                          return (
                            formattedWorkDate === formattedComponentDate &&
                            Number(workMonth) === selectedDate.month() + 1
                          ); // check same month
                        });
                      })

                      .reduce((sum, work) => {
                        // const [hours, minutes] = work.st

                        //   .toString()

                        //   .split(".")

                        //   .map(Number);

                        // return sum + (hours * 60 + (minutes || 0));

                        return sum + Number(work.st);
                      }, 0);

                    const safeTotal = Number(weekTotalMinutes);
                    // const formattedWeekTotal =
                    //   safeTotal > 0
                    //     ? `${Math.floor(safeTotal / 60)}:${String(
                    //         safeTotal % 60
                    //       ).padStart(2, "0")}`
                    //     : "0";
                    // .duration(workDetail.th, "hours")
                    // const formattedWeekTotal = dayjs
                    //   .duration(safeTotal, "hours")
                    //   .format("HH:mm");
                    //                   const formattedWeekTotal = dayjs
                    // .duration(safeTotal, "minutes")
                    // .format("HH:mm");
                    const hours = Math.floor(safeTotal);
                    const minutes = Math.round((safeTotal - hours) * 60);
                    const formattedWeekTotal =
                      hours || minutes
                        ? `${hours}:${String(minutes).padStart(2, "0")}`
                        : "-";

                    return (
                      <React.Fragment key={weekIndex}>
                        <td
                          className={`${
                            stylesList.cellPad
                          } text-center w-32 cursor-pointer  ${(() => {
                            // const weekWorkDetails = item.dailyEntries.filter(
                            //   (work) => {
                            //     const workDate = dayjs(work.date);
                            //     return workDate.isSame(week.start, "week");
                            //   }
                            // );
                            const weekWorkDetails = item.dailyEntries.filter(
                              (work) => {
                                const workDate = dayjs(
                                  work?.date?.toString().slice(0, 4) +
                                    "-" +
                                    work?.date?.toString().slice(4, 6) +
                                    "-" +
                                    work?.date?.toString().slice(6)
                                );
                                return workDate.isSame(week.start, "week");
                              }
                            );

                            const getStatusColor = () => {
                              const statuses = weekWorkDetails.map(
                                (work) => work.timesheetStatus
                              );
                              const counts = statuses.reduce((acc, curr) => {
                                acc[curr] = (acc[curr] || 0) + 1;
                                return acc;
                              }, {});
                              const maxCount = Math.max(
                                ...Object.values(counts)
                              );
                              const maxStatus = Object.keys(counts).find(
                                (key) => counts[key] === maxCount
                              );
                              switch (maxStatus) {
                                case "1":
                                  return "bg-draft-100";
                                case "2":
                                  return "bg-info-100";
                                case "3":
                                  return "bg-success-100";
                                case "4":
                                  return "bg-danger-100";
                                case "5":
                                  return "bg-gray-500";
                                case "6":
                                  return "bg-invoiced-100";
                                default:
                                  return "";
                              }
                            };
                            const weekColor = getStatusColor();
                            // const all
                            // const hasDrafted = item.status == 1;
                            // const hasSubmitted = item.status == 2;
                            // const allApproved = item.status == 3;
                            // const hasRejected = item.status == 4;
                            // const hasJobInactive = item.status == 5;
                            // const hasInvoiced = item.status == 6;

                            // if (hasDrafted) return "bg-draft-100";
                            // if (hasSubmitted) return "bg-info-100";
                            // if (allApproved) return "bg-success-100";
                            // if (hasRejected) return "bg-danger-100";
                            // if (hasJobInactive) return "bg-gray-500";
                            // if (hasInvoiced) return "bg-invoiced-100";
                            // return "";
                            return weekColor;
                          })()}`}
                          onClick={() => toggleWeek(week.weekNum)}
                        >
                          <div
                            className={`flex items-center justify-center px-2

                              `}
                          >
                            <span>{formattedWeekTotal}</span>
                          </div>
                        </td>
                        <AnimatePresence initial={false}>
                          {isExpanded && (
                            <>
                              {week.dates.map((date, dateIndex) => {
                                const workDetail = item.dailyEntries.find(
                                  // (work) =>
                                  //   // dayjs(work.date).format("DD-MM-YYYY") ===
                                  //   // date.format("DD-MM-YYYY")
                                  //  {
                                  //    console.log("date from dailyEntries from db",dayjs(work.date).format("DD-MM-YYYY"))
                                  //   const parsedDate = dayjs(work.date, "YYYYMMDD");

                                  // // Format to desired output: "DD-MM-YYYY"
                                  //     const formattedDate = parsedDate.format("DD-MM-YYYY");
                                  //     console.log("work.date dailYentries from db",String(work.date))
                                  //   console.log("work.date dailYentries",formattedDate)
                                  //   console.log("date from component",dayjs(date).format("DD-MM-YYYY"))
                                  //   return dayjs(work.date).format("DD-MM-YYYY") ===
                                  //  dayjs(date).format("DD-MM-YYYY")
                                  // }
                                  (work) => {
                                    // console.log(
                                    //   "date from dailyEntries from db (raw):",
                                    //   work.date
                                    // );
                                    // if (!work.date) return;
                                    const workDateStr = String(work?.date);
                                    const workYear = workDateStr.substring(
                                      0,
                                      4
                                    );
                                    const workMonth = workDateStr.substring(
                                      4,
                                      6
                                    );
                                    const workDay = workDateStr.substring(6, 8);

                                    const formattedWorkDate = `${workDay}-${workMonth}-${workYear}`;
                                    const inputDate = new Date(date); // assuming `date` is a JS Date object

                                    const day = String(
                                      inputDate.getDate()
                                    ).padStart(2, "0");
                                    const month = String(
                                      inputDate.getMonth() + 1
                                    ).padStart(2, "0"); // months are 0-indexed
                                    const year = inputDate.getFullYear();

                                    const formattedComponentDate = `${day}-${month}-${year}`;

                                    // console.log(
                                    //   "formatted work.date:",
                                    //   formattedWorkDate
                                    // );
                                    // console.log(
                                    //   "formatted component date:",
                                    //   formattedComponentDate
                                    // );

                                    return (
                                      String(formattedWorkDate) ===
                                      String(formattedComponentDate)
                                    );
                                  }
                                );
                                const isCurrentMonth = date.isSame(
                                  selectedDate,
                                  "month"
                                );
                                const getStatusColor = () => {
                                  const status =
                                    item?.dailyEntries?.find(
                                      (work) =>
                                        work?.date ===
                                        Number(date.format("YYYYMMDD"))
                                    )?.timesheetStatus || 0;
                                  switch (status) {
                                    case 1:
                                      return "bg-draft-100";
                                    case 2:
                                      return "bg-info-100";
                                    case 3:
                                      return "bg-success-100";
                                    case 4:
                                      return "bg-danger-100";
                                    case 5:
                                      return "bg-gray-500";
                                    case 6:
                                      return "bg-invoiced-100";
                                    default:
                                      return "";
                                  }
                                };
                                const statusColor = getStatusColor();

                                return (
                                  <motion.td
                                    key={`${weekIndex}-${dateIndex}`}
                                    initial={{ width: 0, opacity: 0 }}
                                    animate={{ width: "auto", opacity: 1 }}
                                    exit={{ width: 0, opacity: 0 }}
                                    transition={{
                                      duration: 0.3,
                                      ease: "easeInOut",
                                    }}
                                    className={`${
                                      stylesList.cellPad
                                    } text-center
                                    ${
                                      !isCurrentMonth
                                        ? "bg-gray-100 text-gray-400"
                                        : ""
                                    }
                                    ${
                                      date.day() === 0 || date.day() === 6
                                        ? "bg-neutrals-100"
                                        : ""
                                    }
                                    ${
                                      isCurrentMonth &&
                                      workDetail &&
                                      date.day() !== 0 &&
                                      date.day() !== 6 &&
                                      `hover:bg-gray-100 cursor-pointer ${`${statusColor}`}`
                                    }

                                  }`}
                                    onClick={
                                      // isCurrentMonth &&
                                      workDetail &&
                                      date.day() !== 0 &&
                                      date.day() !== 6
                                        ? (e) => {
                                            handleCellClick(
                                              e,
                                              // workDetail,
                                              item,
                                              date,
                                              item.name
                                            );
                                          }
                                        : null
                                    }
                                    onMouseDown={(e) =>
                                      handleDragStart(
                                        e,
                                        workDetail,
                                        date,
                                        item.name,
                                        item
                                      )
                                    }
                                    onMouseEnter={(e) =>
                                      handleDragOver(
                                        e,
                                        workDetail,
                                        date,
                                        item.name
                                      )
                                    }
                                    onMouseUp={handleDragEnd}
                                  >
                                    {/* {isCurrentMonth && workDetail */}
                                    {workDetail
                                      ? // ? workDetail.st
                                        dayjs
                                          // .duration(workDetail.th, "hours")
                                          .duration(workDetail.st, "hours")
                                          .format("HH:mm")
                                      : "--"}
                                  </motion.td>
                                );
                              })}
                            </>
                          )}
                        </AnimatePresence>
                      </React.Fragment>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <Modal
        isOpen={showModal}
        width="max-w-7xl"
        onClose={() => {
          setShowModal(false);
          setSelectedUser(null);
          setSelectedCells([]);
        }}
      >
        <ModalData selectedUser={selectedUser} selectedDate={selectedDate} />
      </Modal>
      <Backdrop isOpen={isLoading}>
        <LogoLoader />
      </Backdrop>

      <DrawerAdv
        isOpen={isFilterDrawerOpen}
        onClose={() => setIsFilterDrawerOpen(false)}
        title="Table Filter"
        closeOnOutsideClick
        height="h-[90%]"
        width="2xl:w-7xl xl:w-5xl lg:w-3xl md:w-xl"
      >
        <TableDrawerData
          columns={filterColumns}
          rows={mergedData}
          userId={loginUserUiid}
          tableId="timesheet-full-v1"
          onFilterUpdate={() => {
            // Trigger a reload from localStorage
            loadActiveFilters();
          }}
          onDrawerClose={() => setIsFilterDrawerOpen(false)}
        />
      </DrawerAdv>
    </div>
  );
};

export default TimesheetFull;
